import { useEffect, useId, useMemo, useState } from 'react';
import { Button, Combobox, ConfirmDialog, Skeleton, type ComboboxOption } from '@/components/ui';
import { ExternalTextLink } from '@/components/ui/TextLink';
import { PencilIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import { CompanyLinkFormModal } from '@/components/companies/CompanyLinkFormModal';
import { ApiError, api } from '@/lib/api';
import { currentCycle } from '@/lib/document-naming';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { useCycleOptions } from '@/lib/use-cycle-options';
import type { CompanyLink, CompanyLinkKind } from '@/lib/types';

/**
 * The three kinds, in the order the blocks render. One list so the block
 * headings, the modal titles and the remove sentence cannot word a kind twice.
 */
const KINDS: readonly { kind: CompanyLinkKind; label: string }[] = [
  { kind: 'proposal_excel', label: 'Proposal (Excel)' },
  { kind: 'proposal_deck', label: 'Proposal (deck)' },
  { kind: 'report', label: 'Post-campaign report' },
];

function kindLabel(kind: CompanyLinkKind): string {
  return KINDS.find((entry) => entry.kind === kind)?.label ?? kind;
}

function linkName(link: CompanyLink): string {
  const label = link.label?.trim();
  return label && label !== '' ? label : link.url;
}

function countHint(count: number): string | undefined {
  if (count === 0) return undefined;
  return count === 1 ? '1 link' : `${count} links`;
}

type Outcome = { status: 'ready'; links: CompanyLink[] } | { status: 'error'; message: string };

type Editing =
  | { mode: 'create'; kind: CompanyLinkKind }
  | { mode: 'edit'; link: CompanyLink };

/**
 * The proposal and post-campaign report links on a company, filed PER CYCLE.
 *
 * One cycle picker drives three blocks — the proposal spreadsheet, the deck and
 * the report — each holding any number of links. They are URLs into Drive or
 * SharePoint; nothing is uploaded, so there is no upload control.
 *
 * ⚠️ **A refetch keeps the previous list on screen.** A create, edit or remove
 * re-reads the whole list rather than patching it locally, and showing a
 * skeleton in between would flash the three blocks away on every save.
 *
 * ⚠️ **The picker pins any cycle that appears only in the links.** `useCycleOptions`
 * reads one page of the master, and a `Combobox` whose value matches no option
 * renders its placeholder — so a link on a cycle outside that page would leave
 * the control reading as though nothing were chosen.
 */
export function CompanyCycleLinksField({
  companyId,
  canUpdate,
}: {
  companyId: number;
  canUpdate: boolean;
}) {
  const { ability } = useAuth();
  const selectId = useId();
  const { cycles, loading: cyclesLoading, failed: cyclesFailed } = useCycleOptions();

  const [nonce, setNonce] = useState(0);
  const key = `${companyId}:${nonce}`;
  const [result, setResult] = useState<{ key: string; outcome: Outcome } | null>(null);
  /** The last list that arrived for THIS company, shown while a refetch runs. */
  const [lastLinks, setLastLinks] = useState<{
    companyId: number;
    links: CompanyLink[];
  } | null>(null);
  /** The cycle the reader chose, or `null` to follow the default. */
  const [picked, setPicked] = useState<number | null>(null);

  const [editing, setEditing] = useState<Editing | null>(null);
  const [removing, setRemoving] = useState<CompanyLink | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCompanyLinks(companyId, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setResult({ key, outcome: { status: 'ready', links: response.data } });
        setLastLinks({ companyId, links: response.data });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          key,
          outcome: {
            status: 'error',
            message: caught instanceof Error ? caught.message : 'The links couldn’t be loaded.',
          },
        });
      });
    return () => controller.abort();
  }, [companyId, key]);

  const current = result?.key === key ? result.outcome : null;
  const links: CompanyLink[] | null =
    current?.status === 'ready'
      ? current.links
      : lastLinks?.companyId === companyId
        ? lastLinks.links
        : null;
  const failure = current?.status === 'error' ? current.message : null;

  /* The picker: the master's cycles, plus any cycle only the links name. */
  const choices = useMemo(() => {
    const listed = new Set(cycles.map((cycle) => cycle.id));
    const pinned = new Map<number, string>();
    for (const link of links ?? []) {
      if (!listed.has(link.cycleId) && !pinned.has(link.cycleId)) {
        pinned.set(link.cycleId, link.cycleName);
      }
    }
    return [
      ...cycles.map((cycle) => ({ id: cycle.id, name: cycle.cycleName })),
      ...[...pinned].map(([id, name]) => ({ id, name })),
    ];
  }, [cycles, links]);

  const countByCycle = useMemo(() => {
    const counts = new Map<number, number>();
    for (const link of links ?? []) counts.set(link.cycleId, (counts.get(link.cycleId) ?? 0) + 1);
    return counts;
  }, [links]);

  const cycleOptions: ComboboxOption[] = choices.map((choice) => {
    // No count until the list has arrived: "0 links" during a fetch would be a
    // claim rather than a gap.
    const hint = links ? countHint(countByCycle.get(choice.id) ?? 0) : undefined;
    return { value: String(choice.id), label: choice.name, ...(hint ? { hint } : {}) };
  });

  /*
   * Default: the cycle running today; else the newest cycle that has links
   * (by the cycle's start date, never by id — ids are not in date order); else
   * the first cycle offered.
   */
  const todayCycleId = currentCycle(cycles)?.id ?? null;
  const newestLinkedId = useMemo(() => {
    if (!links || links.length === 0) return null;
    const starts = new Map(cycles.map((cycle) => [cycle.id, cycle.startDate]));
    let best: { id: number; start: string } | null = null;
    for (const link of links) {
      const start = starts.get(link.cycleId);
      if (start === undefined) continue;
      if (!best || start > best.start) best = { id: link.cycleId, start };
    }
    return best?.id ?? links[0]?.cycleId ?? null;
  }, [cycles, links]);

  const selectedCycleId = picked ?? todayCycleId ?? newestLinkedId ?? choices[0]?.id ?? null;
  const selectedCycleName =
    choices.find((choice) => choice.id === selectedCycleId)?.name ?? 'this cycle';
  const cycleLinks = (links ?? []).filter((link) => link.cycleId === selectedCycleId);

  async function confirmRemove() {
    if (!removing || removeBusy) return;
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await api.deleteCompanyLink(companyId, removing.id);
      setRemoving(null);
      setNonce((n) => n + 1);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) {
        // Already gone — the reader's intent is met. Re-read to match.
        setRemoving(null);
        setNonce((n) => n + 1);
      } else if (caught instanceof ApiError && caught.status === 403) {
        setRemoveError(
          `${
            denialReason(ability, 'update', 'companies') ??
            'You no longer have permission to change this account.'
          } Nothing was removed.`,
        );
      } else if (caught instanceof ApiError) {
        setRemoveError(caught.formMessage ?? caught.message);
      } else {
        setRemoveError('Couldn’t reach the server. Nothing was removed.');
      }
    } finally {
      setRemoveBusy(false);
    }
  }

  return (
    <div className="grid min-w-0 gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <label htmlFor={selectId} className="text-xs text-fg-muted">
          Cycle
        </label>
        <div className="w-72 max-w-full min-w-0">
          <Combobox
            id={selectId}
            value={selectedCycleId === null ? '' : String(selectedCycleId)}
            options={cycleOptions}
            placeholder={
              cyclesLoading ? 'Loading cycles…' : cyclesFailed ? 'Cycles unavailable' : 'No cycles'
            }
            searchPlaceholder="Search cycles…"
            disabled={choices.length === 0}
            onChange={(next) => {
              if (next === '') return;
              setPicked(Number(next));
            }}
          />
        </div>
      </div>

      {cyclesFailed && (
        <p role="status" className="m-0 text-xs text-warning">
          Cycle list unavailable — only cycles that already have links are offered.
        </p>
      )}

      {failure !== null && links === null ? (
        <div role="alert" className="flex flex-col items-start gap-2 text-xs text-danger">
          <span className="min-w-0 [overflow-wrap:anywhere]">
            The links couldn’t be loaded. {failure}
          </span>
          <Button size="sm" variant="secondary" onClick={() => setNonce((n) => n + 1)}>
            Try again
          </Button>
        </div>
      ) : links === null ? (
        <div className="grid gap-4">
          {KINDS.map(({ kind }) => (
            <div key={kind} className="grid gap-2">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-4 w-56 max-w-full" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {failure !== null && (
            <div role="alert" className="flex flex-wrap items-center gap-2 text-xs text-danger">
              <span className="min-w-0 [overflow-wrap:anywhere]">
                The links couldn’t be refreshed. {failure}
              </span>
              <Button size="sm" variant="secondary" onClick={() => setNonce((n) => n + 1)}>
                Try again
              </Button>
            </div>
          )}
          <div className="grid gap-4">
            {KINDS.map(({ kind, label }) => {
              const items = cycleLinks.filter((link) => link.kind === kind);
              return (
                <section key={kind} className="grid min-w-0 gap-1.5">
                  <div className="flex min-w-0 items-center justify-between gap-2">
                    <h4 className="m-0 text-xs font-medium text-fg-muted">
                      {label}
                    </h4>
                    {canUpdate && selectedCycleId !== null && (
                      <Button
                        size="sm"
                        variant="ghost"
                        leadingIcon={<PlusIcon />}
                        aria-label={`Add ${label} link`}
                        onClick={() => setEditing({ mode: 'create', kind })}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                  {items.length === 0 ? (
                    <p className="m-0 text-sm text-fg-muted">No link</p>
                  ) : (
                    <ul className="m-0 grid list-none gap-1 p-0">
                      {items.map((link) => {
                        const name = linkName(link);
                        return (
                          <li key={link.id} className="flex min-w-0 items-center gap-1">
                            <ExternalTextLink
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={link.url}
                              className="min-w-0 flex-1 truncate text-sm"
                            >
                              {name}
                            </ExternalTextLink>
                            {canUpdate && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  iconOnly
                                  leadingIcon={<PencilIcon />}
                                  aria-label={`Edit ${name}`}
                                  title={`Edit ${name}`}
                                  onClick={() => setEditing({ mode: 'edit', link })}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  iconOnly
                                  leadingIcon={<TrashIcon />}
                                  aria-label={`Remove ${name}`}
                                  title={`Remove ${name}`}
                                  onClick={() => {
                                    setRemoveError(null);
                                    setRemoving(link);
                                  }}
                                />
                              </>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}

      {editing && selectedCycleId !== null && (
        <CompanyLinkFormModal
          companyId={companyId}
          kind={editing.mode === 'edit' ? editing.link.kind : editing.kind}
          kindLabel={kindLabel(editing.mode === 'edit' ? editing.link.kind : editing.kind)}
          link={editing.mode === 'edit' ? editing.link : null}
          cycleId={editing.mode === 'edit' ? editing.link.cycleId : selectedCycleId}
          cycleName={selectedCycleName}
          cycleOptions={cycleOptions}
          onClose={() => setEditing(null)}
          onSaved={(cycleId) => {
            // Follow the link to the cycle it now sits on, then re-read.
            setPicked(cycleId);
            setNonce((n) => n + 1);
          }}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        onClose={() => {
          if (removeBusy) return;
          setRemoving(null);
          setRemoveError(null);
        }}
        onConfirm={() => void confirmRemove()}
        title="Remove this link?"
        description={
          removing
            ? `“${linkName(removing)}” will be removed from ${kindLabel(removing.kind)} for ${removing.cycleName}.`
            : ''
        }
        confirmLabel="Remove"
        loading={removeBusy}
        error={removeError}
      />
    </div>
  );
}
